import { useEffect, useRef, useState } from 'react'
import { Link, NavLink } from 'react-router'
import type { JSX } from 'react'
import { useAuth } from '@/auth/context'
import { useBrand } from '@/brand/context'
import { faviconFor } from '@/brand/favicon'
import { ThemeToggle } from '@/components/ThemeToggle'
import { initials } from '@/lib/format'
import { NotificationBell } from './NotificationBell'
import { OrgSwitcher } from './OrgSwitcher'

export interface Crumb {
  label: string
  to?: string
}

/**
 * Breadcrumbs carry the organisation, because agency owners and support staff
 * both work across several and "which tenant am I in" must be answerable
 * without looking at the sidebar (S-D3).
 */
export function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }): JSX.Element {
  return (
    <nav className="ar-crumbs" aria-label="Breadcrumb">
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1
        return (
          <span key={`${crumb.label}-${String(index)}`} style={{ display: 'contents' }}>
            {crumb.to && !last ? (
              <Link to={crumb.to}>{crumb.label}</Link>
            ) : (
              <span aria-current={last ? 'page' : undefined}>{crumb.label}</span>
            )}
            {last ? null : <span aria-hidden="true">/</span>}
          </span>
        )
      })}
    </nav>
  )
}

/**
 * The rows of the account menu: *Settings*, *Billing*, the theme switch, *Sign
 * out*.
 *
 * One component for two homes (parity P-MOB-01, 2026-09-13). Above 1100px they
 * are `AccountMenu`'s `role="menu"` rows behind the avatar; below it the avatar
 * is gone — `shell.html:236-241` draws the compact bar as *☰ · mark · bell* and
 * nothing else — and the same rows sit in the navigation drawer's foot, where
 * they are a plain list. `role` says which, so the drawer's copy does not
 * carry `menuitem` outside a menu.
 */
export function AccountItems({
  role,
  onNavigate,
}: {
  role?: 'menuitem'
  /** Called on any row that leaves — the menu and the drawer both close with it. */
  onNavigate: () => void
}): JSX.Element {
  const { signOut } = useAuth()
  return (
    <>
      <NavLink to="/settings/organisation" role={role} onClick={onNavigate}>
        Settings
      </NavLink>
      <NavLink to="/billing" role={role} onClick={onNavigate}>
        Billing
      </NavLink>
      <ThemeToggle menuitem role={role} />
      <button
        type="button"
        role={role}
        onClick={() => {
          onNavigate()
          void signOut()
        }}
      >
        Sign out
      </button>
    </>
  )
}

/**
 * Who the account rows belong to: the address, and the unconfirmed badge when
 * it applies. Shared by the menu and the drawer for the reason `AccountItems`
 * gives.
 */
export function AccountWho(): JSX.Element {
  const { user } = useAuth()
  const label = user?.email ?? user?.mobile ?? 'Your account'
  return (
    <div className="app-menu__who">
      <strong>{label}</strong>
      {user && !user.email_verified && user.email ? (
        <span className="ar-badge ar-badge--warning">Address not confirmed</span>
      ) : null}
    </div>
  )
}

/**
 * The account menu.
 *
 * Closes on Escape and on a click anywhere else, both of which a menu that only
 * toggles on its own button fails to do — and the second is the one people
 * actually hit, because they click the thing they wanted instead of closing the
 * menu first.
 *
 * **The avatar is a button and stays one** (SHELL-20). `shell.html:104` draws a
 * bare `span.ar-avatar`, and it is drawn that way because the prototype has no
 * menu behind it — there is no sign-out and no billing link anywhere on that
 * screen. A `span` here would mean the only route out of a session is the
 * keyboard, so the departure is the mockup's omission rather than this
 * component's. Kept as an app extra (parity 2026-09-13, owner's rule: nothing
 * is cut) and hidden below 1100px, where the drawer carries the same rows.
 *
 * **`Switch organisation` was removed from here on 2026-09-10**, when the owner
 * asked for the switch to live in the top right as a control of its own.
 * `OrgSwitcher` is that control, eight pixels to the left, and it does the job
 * better than this item could: it names the organisation you are in without
 * being opened, and it carries *Set as default*, which has nowhere to go in a
 * flat list of menu rows. Two controls for one action is the thing worth
 * avoiding — a user who finds the item here learns a slower route and stops
 * looking for the faster one.
 *
 * **The theme control lives in here rather than in the bar** (SHELL-18) — see
 * `ThemeToggle` on why moving it was the honest reading of a mockup that
 * predates it.
 *
 * Exported for the settings frame (`SettingsShell`), which has no drawer to
 * carry these rows below 1100px and so keeps the avatar at every width.
 */
export function AccountMenu(): JSX.Element {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDocument(event: MouseEvent | KeyboardEvent): void {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false)
        return
      }
      if (event.target instanceof Node && !wrapper.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocument)
    document.addEventListener('keydown', onDocument)
    return () => {
      document.removeEventListener('mousedown', onDocument)
      document.removeEventListener('keydown', onDocument)
    }
  }, [open])

  const label = user?.email ?? user?.mobile ?? 'Your account'

  return (
    <div className="app-menu" ref={wrapper}>
      <button
        type="button"
        className="ar-btn ar-btn--quiet ar-btn--sm ar-btn--icon"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${label}`}
        onClick={() => {
          setOpen((current) => !current)
        }}
      >
        <span className="ar-avatar ar-avatar--sm" aria-hidden="true">
          {initials(label)}
        </span>
      </button>
      {open ? (
        <div className="app-menu__panel" role="menu">
          <AccountWho />
          <div className="app-menu__items">
            <AccountItems
              role="menuitem"
              onNavigate={() => {
                setOpen(false)
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The top bar: a menu button below 1100px, breadcrumbs, notifications, account.
 *
 * The menu button is rendered here rather than in a separate mobile header so
 * that there is one top bar at every width — two headers means two places for
 * the trail to be wrong, and two components to keep in step.
 *
 * **What that one bar shows does change with width** (SHELL-31), which is the
 * half this used to refuse. `shell.html:236-241` draws the compact bar as *☰ ·
 * brand mark · spacer · bell*, and the reason is measurable: at 390px a
 * four-crumb trail wrapped onto three lines and pushed everything beside it off
 * the row. So below 1100px the trail is replaced by the mark — both are always
 * rendered and the stylesheet picks, so there is still one component and one
 * source for each.
 *
 * **The account menu is hidden below 1100px** (parity P-MOB-01, 2026-09-13):
 * the compact bar is drawn without an avatar. Its rows are not lost — the
 * navigation drawer's foot carries `AccountItems`, so *sign out* and *Billing*
 * stay one press further than the ☰ rather than nowhere.
 *
 * **`OrgSwitcher` is kept at every width for the same reason** (2026-09-10). It
 * collapses to its avatar below 1100px, where the trail it sits beside is
 * replaced by the brand mark — so at that width it is the only thing on screen
 * naming the organisation, which is exactly when being able to see and change
 * it matters most.
 *
 * **Both icon buttons are `ghost`, not `quiet`** (SHELL-17, SHELL-30):
 * `shell.html:103` and `:237` give them a surface and a `--ar-border-strong`
 * edge. On the ☰ that is the difference between a visible control and a glyph
 * floating on the bar — and it is the only way to the navigation at that width.
 */
export function TopBar({
  crumbs,
  onOpenMenu,
}: {
  crumbs: Crumb[]
  onOpenMenu: () => void
}): JSX.Element {
  const brand = useBrand()
  return (
    <header className="app-top">
      <button
        type="button"
        className="ar-btn ar-btn--ghost ar-btn--sm ar-btn--icon app-menu-button"
        aria-label="Open navigation"
        onClick={onOpenMenu}
      >
        <span aria-hidden="true">☰</span>
      </button>
      <Breadcrumbs crumbs={crumbs} />
      {/* SHELL-31. The compact bar's own identity, shown only where the trail is
          hidden. **The mark alone, not the lockup** — `shell.html:238` draws the
          glyph at 1.5rem with no wordmark beside it, and the word would eat the
          row the trail was removed to free. `aria-hidden`, because it names the
          product rather than the page: what tells a screen-reader user where
          they are at this width is the screen's own `h1` and the bottom bar's
          `aria-current`, and an alt here would announce the brand on every
          screen instead.

          The same image the tab icon carries (spec: *Branding*): AugRank's
          mark on the apex, the organisation's logo on its host, and the
          letter mark from its name when it has uploaded none — so nothing on
          a tenant host draws AugRank. The reserved box is the apex mark's
          92×116 viewBox or the letter mark's square; a logo of any other
          ratio still renders at 1.5rem high with `width: auto`. */}
      <span className="app-top__mark" aria-hidden="true">
        <img
          src={faviconFor(brand)}
          alt=""
          width={brand.host === 'tenant' ? 64 : 92}
          height={brand.host === 'tenant' ? 64 : 116}
          style={{ height: '1.5rem', width: 'auto' }}
        />
      </span>
      <span className="app-spacer" />
      <OrgSwitcher />
      <NotificationBell />
      <AccountMenu />
    </header>
  )
}
